import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://centdouze.ca";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * <SEO /> — per-page SEO override using react-helmet-async.
 * Updates <title>, meta description/og/twitter, canonical, robots.
 *
 * Props:
 *   title         — page title (suffixed with brand)
 *   description   — meta description (overrides default)
 *   path          — relative path (e.g. "/", "/brief/abc"). Used for canonical
 *   ogSlug        — slug used by /api/og/{slug}.png on backend (e.g. "home", "brief/SC-XXXXXX")
 *   ogImageUrl    — explicit absolute URL (overrides ogSlug)
 *   noindex       — bool — adds <meta name="robots" content="noindex,nofollow"/>
 *   type          — "website" | "article" | ... (defaults to "website")
 */
export default function SEO({
    title,
    description,
    path = "/",
    ogSlug,
    ogImageUrl,
    noindex = false,
    type = "website",
}) {
    const fullTitle = title
        ? `${title} — Studio Cent Douze`
        : "Studio Cent Douze · Studio créatif à Magog";

    const finalDescription =
        description ||
        "Studio Cent Douze — studio créatif à Magog. Création de sites web, photo, vidéo, création de contenu et identité de marque pour les PME du Québec.";

    const canonical = `${SITE_URL.replace(/\/$/, "")}${path}`;

    const ogImage =
        ogImageUrl ||
        (ogSlug && BACKEND_URL
            ? `${BACKEND_URL.replace(/\/$/, "")}/api/og/${ogSlug}.png`
            : `${BACKEND_URL.replace(/\/$/, "")}/api/og/home.png`);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={finalDescription} />
            <link rel="canonical" href={canonical} />

            {noindex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Studio Cent Douze" />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={finalDescription} />
            {ogImage && <meta property="og:image" content={ogImage} />}
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:locale" content="fr_CA" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={finalDescription} />
            {ogImage && <meta name="twitter:image" content={ogImage} />}
        </Helmet>
    );
}
